import { forwardRef, type ReactNode } from 'react';
import { motion, type HTMLMotionProps } from 'motion/react';
import { spring } from '../motion/presets';
import { cx } from '../internal/cx';
import { Spinner } from './Spinner';

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger';
export type ButtonSize = 'sm' | 'md' | 'lg';

export type ButtonProps = Omit<HTMLMotionProps<'button'>, 'children'> & {
  variant?: ButtonVariant;
  size?: ButtonSize;
  /** Affiche un spinner + `aria-busy` et désactive le bouton. */
  loading?: boolean;
  iconLeft?: ReactNode;
  iconRight?: ReactNode;
  children?: ReactNode;
};

const VARIANTS: Record<ButtonVariant, string> = {
  primary:
    'bg-panda-500 text-ink-950 font-semibold hover:shadow-glow-panda hover:brightness-110',
  secondary:
    'bg-ink-700 text-ink-100 border border-ink-600 hover:border-panda-500/60 hover:shadow-glow-panda',
  ghost: 'bg-transparent text-ink-100 hover:bg-ink-100/10',
  danger: 'bg-danger-500 text-white font-semibold hover:brightness-110',
};

const SIZES: Record<ButtonSize, string> = {
  sm: 'h-9 px-4 text-sm gap-1.5',
  md: 'h-11 px-5 text-base gap-2', // 44px → cible tactile OK
  lg: 'min-h-[3.25rem] px-7 text-lg gap-2.5',
};

/**
 * Bouton pill du design system. `whileTap` scale .96 (spring snappy),
 * halo au hover, focus visible via l'anneau global.
 */
export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    variant = 'primary',
    size = 'md',
    loading = false,
    iconLeft,
    iconRight,
    disabled,
    className,
    children,
    type = 'button',
    ...rest
  },
  ref,
) {
  const isDisabled = Boolean(disabled) || loading;
  return (
    <motion.button
      ref={ref}
      type={type}
      disabled={isDisabled}
      aria-busy={loading || undefined}
      whileTap={isDisabled ? undefined : { scale: 0.96 }}
      transition={spring.snappy}
      className={cx(
        'inline-flex select-none items-center justify-center rounded-full font-display',
        'transition-[box-shadow,filter,background-color,border-color] duration-fast ease-fun',
        'disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:shadow-none disabled:hover:brightness-100',
        VARIANTS[variant],
        SIZES[size],
        className,
      )}
      {...rest}
    >
      {loading ? (
        <Spinner size="sm" label="Chargement…" />
      ) : (
        iconLeft && <span aria-hidden="true">{iconLeft}</span>
      )}
      {children}
      {iconRight && !loading && <span aria-hidden="true">{iconRight}</span>}
    </motion.button>
  );
});
